import React, { useState, useEffect } from 'react';
import { observer } from 'mobx-react';
import { toZonedDateTime } from '@ms-rewards/date-lib';
import { Button } from '@components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle
} from '@components/ui/dialog';
import { DatePicker } from '@components/date-picker';
import { Select } from '@components/select';
import { TextField } from '@components/textfield';
import { getTimezoneOptions } from '@components/utils/time';
import { injectTSDI } from '../../../../../core/tsdi';
import { I18n } from '../../../../../core/i18n';
import { useStore } from './store';

interface AddStepsProps {
    open?: boolean;
    onModalClose?(): void;
    onSuccess?: () => void;
}

export const AddSteps = observer(
    ({ open: initialOpen = false, onModalClose, onSuccess }: AddStepsProps) => {
        const [open, setOpen] = useState(initialOpen);
        const { fields, errors, onSubmitHandler, addStepsRequest } = useStore(
            () => {
                handleClose();
                onSuccess?.();
            }
        );
        const { __ } = injectTSDI(I18n);

        const { stepCount, userDay, timeZone } = fields;

        const handleClose = () => {
            setOpen(false);
            onModalClose?.();
        };

        useEffect(() => {
            setOpen(initialOpen);
        }, [initialOpen]);

        const handleSubmit = async () => {
            await onSubmitHandler();
        };

        return (
            <Dialog open={open} onOpenChange={(next) => !next && handleClose()}>
                <DialogContent className="sm:max-w-lg">
                    <DialogHeader>
                        <DialogTitle>Add Steps</DialogTitle>
                    </DialogHeader>
                    <div className="grid gap-4 md:grid-cols-3">
                        <div className="md:col-span-2">
                            <DatePicker
                                label="Day"
                                value={
                                    userDay.value
                                        ? userDay.value.toDate()
                                        : null
                                }
                                onChange={(value) => {
                                    if (value == null) {
                                        return;
                                    }
                                    void userDay.onChange(
                                        toZonedDateTime(
                                            value,
                                            timeZone.value
                                        ).startOf('day')
                                    );
                                }}
                            />
                        </div>
                        <Select
                            label="Timezone"
                            value={timeZone.value}
                            options={getTimezoneOptions()}
                            onChange={(value) => {
                                timeZone.onChange(value);
                            }}
                        />
                        <div className="md:col-span-3">
                            <TextField
                                value={stepCount.value}
                                onChange={stepCount.onChange}
                                type="number"
                                label="Step count"
                                required
                                error={
                                    stepCount.error &&
                                    __(stepCount.error as I18nKey, 0)
                                }
                                helperText
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={handleClose}
                            disabled={addStepsRequest.loading}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleSubmit}
                            loading={addStepsRequest.loading}
                            disabled={Boolean(errors)}
                        >
                            Save
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        );
    }
);
